/**
 * GAU (AI credit) pack catalog for self-serve checkout surfaces.
 *
 * Lives in core/ because both the dashboard (Manage Plan) and core surfaces
 * (credit-card gate, AI quota paywall) sell the same packs — core must not
 * import from dashboard. Prices are intentionally hardcoded to match the
 * current Stripe price list (same trade the Manage Plan modal already made).
 */
export type AiCreditPackOption = {
  id: string;
  credits: number;
  priceUsd: number;
  billingInterval: "annual" | "monthly";
};

export const AI_CREDIT_PACK_OPTIONS: AiCreditPackOption[] = [
  {
    id: "monthly-100",
    credits: 100,
    priceUsd: 125,
    billingInterval: "monthly",
  },
  {
    id: "monthly-250",
    credits: 250,
    priceUsd: 313,
    billingInterval: "monthly",
  },
  {
    id: "monthly-500",
    credits: 500,
    priceUsd: 625,
    billingInterval: "monthly",
  },
  {
    id: "monthly-1000",
    credits: 1000,
    priceUsd: 1250,
    billingInterval: "monthly",
  },
  {
    id: "monthly-2500",
    credits: 2500,
    priceUsd: 3125,
    billingInterval: "monthly",
  },
  {
    id: "monthly-3500",
    credits: 3500,
    priceUsd: 4375,
    billingInterval: "monthly",
  },
  {
    id: "monthly-5000",
    credits: 5000,
    priceUsd: 6250,
    billingInterval: "monthly",
  },
  {
    id: "monthly-7500",
    credits: 7500,
    priceUsd: 9375,
    billingInterval: "monthly",
  },
  {
    id: "annual-100",
    credits: 100,
    priceUsd: 1200,
    billingInterval: "annual",
  },
  {
    id: "annual-250",
    credits: 250,
    priceUsd: 3000,
    billingInterval: "annual",
  },
  {
    id: "annual-500",
    credits: 500,
    priceUsd: 6000,
    billingInterval: "annual",
  },
  {
    id: "annual-1000",
    credits: 1000,
    priceUsd: 12000,
    billingInterval: "annual",
  },
  {
    id: "annual-2500",
    credits: 2500,
    priceUsd: 30000,
    billingInterval: "annual",
  },
  {
    id: "annual-3500",
    credits: 3500,
    priceUsd: 42000,
    billingInterval: "annual",
  },
  {
    id: "annual-5000",
    credits: 5000,
    priceUsd: 60000,
    billingInterval: "annual",
  },
  {
    id: "annual-7500",
    credits: 7500,
    priceUsd: 90000,
    billingInterval: "annual",
  },
];

/** Pack options for one billing interval, smallest bucket first. */
export function creditPackOptionsForInterval(
  billingInterval: "annual" | "monthly",
): AiCreditPackOption[] {
  return AI_CREDIT_PACK_OPTIONS.filter(
    (option) => option.billingInterval === billingInterval,
  ).sort((a, b) => a.credits - b.credits);
}

/**
 * The smallest purchasable pack for an interval — the default selection on
 * seatless (credits-pricing) checkout surfaces, pending a product decision
 * on a dedicated minimum bucket.
 */
export function defaultCreditPack(
  billingInterval: "annual" | "monthly",
): AiCreditPackOption {
  return creditPackOptionsForInterval(billingInterval)[0];
}

/**
 * The pack an org is committed to, derived from its current pool size
 * (plan-summary `currentPlanCredits`). The pool folds in top-ups, so snap to
 * the largest bucket at or below it; unknown or empty pools read as the
 * minimum pack (the only pack a gated trial can start on).
 */
export function committedPackCredits(
  poolCredits: number | null | undefined,
): number {
  const buckets = creditPackOptionsForInterval("monthly")
    .map((option) => option.credits)
    .filter((credits) => poolCredits != null && credits <= poolCredits);
  return buckets.length > 0
    ? buckets[buckets.length - 1]
    : defaultCreditPack("monthly").credits;
}

/**
 * The pack a checkout actually submits. Free trials always start on the
 * minimum pack — the picker is a buy-now affordance, mirroring how the seat
 * stepper only applies in buy-now mode (trials pin to 1 seat). Buckets are
 * interval-independent, so this needs no billingInterval.
 */
export function effectiveCheckoutPackCredits({
  buyNow,
  selected,
}: {
  buyNow: boolean;
  selected: number;
}): number {
  return buyNow ? selected : defaultCreditPack("monthly").credits;
}

/**
 * Effective per-GAU price implied by the pack catalog, in cents. TEAMS-style
 * self-serve plans bill flat subscriptions rather than metered usage, so
 * usage charts that want a dollar denomination use this pack ratio as the
 * display price ($1.25/GAU across the monthly catalog). Uses the pack
 * matching `credits` when one exists, else the default monthly pack.
 */
